/* eslint-disable max-statements, complexity, max-lines, max-depth */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import config from "../../../../../share/config";
import api from "../../../../../share/util/api/service.js";
import {
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_FAILED,
} from "../../../../../share/util/api/polling";
import session from "../../../../../share/session";
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import dataConversion from "../../../../../share/util/didDataConversion";
import {
  API_TYPE,
  DATA_TYPE,
  ERR_KEY,
  FLOW,
  MNTR_STATUS,
  NRC_CODE,
  RES_TYPE,
  HEX_PREFIX,
  FLOW_ERROR,
  REQUEST_RESULT,
  ERR_TYPE,
  AT_EXE_TYPE
} from "../../../../pages/individualActiveTest/individualActiveTestConstants.js";
import atUtil from "../../../../pages/individualActiveTest/individualActiveTestUtil";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import { fixNumberWithDecimal } from "../../../../../common/nodejs/mathFunctions";

const PARAM = {
  ID: "active_test_id",
  ID_REQUIREMENT: "requirement_id",
  DID: "did",
  RID: "rid",
  CTRL_STATE: "control_state",
  EXE_MNTR_TERMINATE: "execution_monitoring_terminate_condition",
  EXE_MNTR_CONTINUE: "execution_monitoring_continuate_condition",
  STOP_MNTR_TERMINATE: "stop_monitoring_terminate_condition",
  STOP_MNTR_CONTINUE: "stop_monitoring_continuate_condition",
  WAIT_TIME: "wait_time",
  RANGE: "operations_rules",
  MIN: "min",
  MAX: "max",
  STEP: "step",
  TYPE: "data_type",
  UNIT: "unit_id",
  USER_DATA: "user_data",
  VAL: "value",
  RES_TYPE: "res_type",
  READOUT: "readout_data",
  RECORD: "data_record",
  BYTE_OR_BIT: "byte_or_bit",
  SID0X2E_0X2F: "sid0x2E_sid0x2F",
  MNTR_STATUS: "monitoring_status",
  BYTE_SIZE: "byte_size",
  LITTLE_ENDIAN: "little_endian",
  OPERATION_RULES: "operations_rules",
  START_BYTE_POSITION: "start_byte_position",
  REQUEST_RESULT: "request_result",
};

const UNIT_STR = {
  NONE: "[-]",
  PRE: "[",
  AF: "]",
};

const UIID = {
  ACC: "90108", // オルタネータ制御コントロール
  RSRH: "901", // 右ステアリング連動ヘッドランプ(SRH)
  LSRH: "902", // 左ステアリング連動ヘッドランプ(SRH)
  RADB: "903", // 右アダプティブドライビングビーム(ADB)
  LADB: "904", // 左アダプティブドライビングビーム(ADB)
  ENF: "1201", // 強制発音 
};

const SP9_VALS = {
  901: [-5, 0, 5, 10, 15, 18], // 右ステアリング連動ヘッドランプ(SRH)
  902: [-18, -15, -10, -5, 0, 5], // 左ステアリング連動ヘッドランプ(SRH)
  903: [-2, 0, 2, 5], // 右アダプティブドライビングビーム(ADB)
  904: [-5, -2, 0, 2], // 左アダプティブドライビングビーム(ADB)
};

const SP12_VALS = {
  "1201": ["1049_1_all", "1048_1_all", "1045_1_all", "1047_1_all", "104A_1_all"] // 強制発音
};

const ACC_VALS = {
  // オルタネータ制御コントロール 表示値
  LOW: "02", // Low
  MID: "03", // Mid
  HIGH: "00", // High
  EXHIGH: "01", // ExHigh
};

export default {
  name: "activeTestSliderPanel",
  data() {
    return {
      flow: FLOW.INIT,
      // ユーザ入力値（step数）
      userInputStep: null,
      didList: [],
      // 変換情報
      conversionInfoList: [],
      message: "",
      // コントロールパネル情報
      panelInfo: {},
      // 値の翻訳ファイル {val:10進数, label:表示値}
      inputValI18nList: [],
      // スライダー設定
      slider: {
        unit: "",
        // 選択範囲
        range: {
          min: null, // ステップ数0
          max: null, // ステップ数最大
          step: null, // ステップ
          stepVal: null, // 変換後のステップ
          minVal: null, // 変換後の最小値
          maxVal: null, // 変換後の最大値
        },
        // 目盛り
        option: [],
      },
      // メモ用計測ID
      measured_id: "",
      // 停止中フラグ(doStart, doStop以外で変更してはならない)
      isStopping: false,
      displayProcessing: false,
      // サインアウト
      signout: false,
      //実行処理から遷移
      isExecution: false,
    };
  },
  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ、親画面の画面遷移判定で使用
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
    // DM開始時刻
    baseTime: { type: Number, default: null, required: false },
  },

  components: {
    "processing-view": loading,
  },

  watch: {
    // 言語設定変更時処理
    language: {
      handler() {
        this.clearAtMsg();
        if (this.conversionInfoList && this.conversionInfoList.length > 0) {
          const conversionInfo = this.conversionInfoList.find(
            (ci) => ci[PARAM.DID] === this.selectedAtFunc[PARAM.SID0X2E_0X2F][0]
          );

          // 入力値の翻訳表を取得
          this.inputValI18nList = this.getDidI18nList(conversionInfo);

          this.setSliderSetting(
            this.selectedAtFunc,
            conversionInfo,
            this.inputValI18nList
          );
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      vehicleInfo: (state) => state.vehicle.vehicleInfo,
      individualMenus: (state) => state.vehicle.individualMenus,
      isTestMode: (state) => state.netAppConnector.isTestMode,
    }),
    projectId() {
      return this.vehicleInfo ? this.vehicleInfo.vehicle.project.id : null;
    },
    activeTestId() {
      return this.selectedAtFunc[PARAM.ID];
    },
    requirementId() {
      return this.selectedAtFunc[PARAM.ID_REQUIREMENT];
    },
    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    rid() {
      return this.selectedAtFunc[PARAM.RID];
    },
    controlState() {
      return this.selectedAtFunc[PARAM.CTRL_STATE];
    },
    dataType() {
      if (this.conversionInfoList && this.conversionInfoList[0]) {
        const conversionInfo = this.getConversionInfo() ? this.getConversionInfo().info : this.conversionInfoList[0].info;
        return conversionInfo[PARAM.TYPE];
      }
      else if (this.requirementId === "REQ.ACT.SP5")
        return DATA_TYPE.HEX;
      return null;
    },
    isDriveFlow() {
      return [
        FLOW.PRE_EXECUTE,
        FLOW.EXECUTE,
        FLOW.CHECK_EXE,
      ].includes(this.flow);
    },
    driveBtnLabel() {
      return !this.isDriveFlow
        ? this.$t("individual.button_active_test_drive")
        : this.$t("individual.button_data_monitor_stop");
    },
    // 画面表示するユーザ入力値
    dispVal() {
      if (!this.dataType) return "";

      let didVal = this.userInputStep;
      if (DATA_TYPE.FLG === this.dataType) {
        // 翻訳リストから表示値を取得
        didVal = this.userInputStep === 255 ? 1 : Number(this.userInputStep);
        const ele = this.inputValI18nList.find((e) => e.val === didVal);
        return ele ? ele.label : "";
      } else if (
        // didに対応する翻訳した表示値を取得
        DATA_TYPE.HEX === this.dataType ||
        this.requirementId === "REQ.ACT.SP9" ||
        this.requirementId === "REQ.ACT.SP12"
      ) {
        const ele =
          this.inputValI18nList && this.inputValI18nList.length > didVal
            ? this.inputValI18nList[didVal]
            : null;
        return ele ? ele.label : "";
      } else if (DATA_TYPE.VAL === this.dataType) {
        // スライダー値から算出して表示値を取得
        return this.getSliderValue();
      }

      return "";
    },
    // 画面表示する最小値
    dispMin() {
      if (!this.dataType) return "";
      if (
        DATA_TYPE.HEX === this.dataType ||
        DATA_TYPE.FLG === this.dataType
      ) {
        const ele =
          this.inputValI18nList &&
            this.inputValI18nList.length > this.slider.range.min
            ? this.inputValI18nList[this.slider.range.min]
            : null;
        return ele ? ele.label : "";
      } else if (
        DATA_TYPE.VAL === this.dataType &&
        this.requirementId === "REQ.ACT.SP9"
      ) {
        const ele =
          this.inputValI18nList &&
            this.inputValI18nList.length > this.slider.range.min
            ? this.inputValI18nList[this.slider.range.min]
            : null;
        const ret = ele ? ele.label + this.slider.unit : this.slider.unit;
        return ret;
      } else if (DATA_TYPE.VAL === this.dataType) {
        return this.slider.range.minVal + this.slider.unit;
      } else {
        return "";
      }
    },
    // 画面表示する最大値
    dispMax() {
      if (!this.dataType) return "";
      if (
        DATA_TYPE.HEX === this.dataType ||
        DATA_TYPE.FLG === this.dataType
      ) {
        const ele =
          this.inputValI18nList &&
            this.inputValI18nList.length > this.slider.range.max
            ? this.inputValI18nList[this.slider.range.max]
            : null;
        return ele ? ele.label : "";
      } else if (
        DATA_TYPE.VAL === this.dataType &&
        this.requirementId === "REQ.ACT.SP9"
      ) {
        const ele =
          this.inputValI18nList &&
            this.inputValI18nList.length > this.slider.range.max
            ? this.inputValI18nList[this.slider.range.max]
            : null;
        const ret = ele ? ele.label + this.slider.unit : this.slider.unit;
        return ret;
      } else if (DATA_TYPE.VAL === this.dataType) {
        return this.slider.range.maxVal + this.slider.unit;
      } else {
        return "";
      }
    },
    disabled() {
      const disabled = {
        input: ![FLOW.STANDBY].includes(this.flow),
        drive: ![
          FLOW.STANDBY,
          FLOW.EXECUTE,
          FLOW.CHECK_EXE,
        ].includes(this.flow),
      };
      return disabled;
    },
    // HEV系ATのDM表示に用いる
    isHev() { return this.atInfo && (this.atInfo.length > 0); },
    atInfo() { return this.selectedAtFunc?.at_info; },
  },
  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * AT項目選択ボタンクリック時の処理
     * AT項目選択 → DM項目選択 遷移時に行う処理を記述する
     */
    btnAtSelectOnClicked() {
      // データ読み出し実行フラグをfalseに切り替え
      this.$emit("toggle-read-executed", false);
      // 初期情報取得処理
      this.getInitInfo();
    },

    // Control Stateのバイト数
    byteSize() {
      const conversionInfo = this.getConversionInfo() ? this.getConversionInfo().info : this.conversionInfoList[0].info;
      return conversionInfo.byte_or_bit === "byte" ? conversionInfo.byte_size : Math.ceil(conversionInfo.bit_size / 8);
    },

    changeSlider(event) {
      const val = Number(event.target.value);
      this.userInputStep = val;
    },
    upBtn() {
      const minInt = this.getNumVal(this.slider.range[PARAM.MIN]);
      const maxInt = this.getNumVal(this.slider.range[PARAM.MAX]);
      const stepInt = this.getNumVal(this.slider.range[PARAM.STEP]);
      if (DATA_TYPE.FLG === this.dataType) {
        if (this.userInputStep < this.byteSize()) {
          this.userInputStep = this.byteSize();
        }
      } else {
        if (maxInt >= minInt + (this.userInputStep + 1) * stepInt) {
          this.userInputStep++;
        }
      }
    },
    downBtn() {
      if (DATA_TYPE.FLG === this.dataType) {
        if (this.userInputStep > 0) {
          this.userInputStep = 0;
        }
      } else {
        if (this.userInputStep > 0) {
          this.userInputStep--;
        }
      }
    },

    driveBtn() {
      if (!this.isDriving) {
        // 駆動
        this.startDrive();
      } else {
        // 停止
        this.stopDrive();
      }
    },

    /** エラー時のAT終了処理 エラーメッセージ表示処理内で実行 */
    errTerminate() {
      this.$emit("err-terminate");
    },

    getNumVal(val) {
      if (!val || isNaN(val)) return 0;
      return Number(val);
    },

    getNumValWithDigitAfterDecimal(val, digitAfterDecimal) {
      if (!val || isNaN(val)) return 0;
      if (!digitAfterDecimal) return parseInt(val);
      return Math.floor(Number(val), digitAfterDecimal);
    },

    /**
     * 初期表示処理
     * flowをSTANDBYへ遷移させ、駆動ボタンの表示を「駆動」に切り替える
     */
    displayInit() {
      // 待機に遷移
      this.flow = FLOW.STANDBY;
      this.$emit("toggle-drive", false);
    },

    /** 駆動ボタン押下処理 */
    async startDrive() {
      this.$emit("toggle-drive", true);
      this.flow = FLOW.PRE_EXECUTE;
      this.clearAtMsg();

      // 開始マーカの登録実行
      const markInfo = {
        "type": AT_EXE_TYPE.START, // 開始をセット
        "info": [
          {
            "param": this.$t("individual.label_active_test_target"),
            "value": this.dispVal + this.slider.unit
          }
        ]
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await atUtil.doStandby(this, true);
      if (response) {
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            {
              let checkResponse = true;
              if (response.data.user_data && Array.isArray(response.data.user_data)) {
                for (const userData of response.data.user_data) {
                  if (userData.res_type !== RES_TYPE.OK) {
                    checkResponse = false;
                    break;
                  }
                }
              }
              // 正常終了
              if (checkResponse) {
                // 自動停止or停止操作待ちの分岐
                if (await this.doStart()) {
                  if (!this.isStopping) {
                    const response = await this.doStop();
                    if (response) {
                      if (response.data.status === config.REQUEST_STATUS_COMPLETED) {
                        const result = await this.doTerminate();
                        if (result) {
                          this.$emit("toggle-drive", false);
                          this.flow = FLOW.STANDBY;
                        } else {
                          // 共通エラーへ遷移
                          this.flow = FLOW.ERR;
                          this.commonError();
                        }
                      }
                    }
                  }
                }
              }
              else {
                // 共通エラーへ遷移
                this.flow = FLOW.ERR;
                this.commonError();
              }
            }
            break;
          default:
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.commonError();
            break;
        }
      }
    },

    /** 停止ボタン押下処理 */
    async stopDrive() {
      this.flow = FLOW.STOP;

      // 停止マーカの登録実行
      const markInfo = {
        "type": AT_EXE_TYPE.END, // 停止をセット
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await this.doStop();

      // HEV系AT対応: 停止処理完了時、IO/Routine Control項目の値を空にする
      switch (this.requirementId) {
        case "REQ.ACT-9":
        case "REQ.ACT-11":
        case "REQ.ACT-26":
          this.$emit("dm-change-to-blank");
          break;
        default:
          break;
      }

      if (response) {
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          const result = await this.doTerminate();
          if (result) {
            this.$emit("toggle-drive", false);
            this.flow = FLOW.STANDBY;

            // アクティブ文言のクリア判定
            if (atUtil.isClearMessageOnStop(this.requirementId)) {
              this.clearAtMsg();
            }
          } else {
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.commonError();
          }
        }
      } else {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.commonError();
      }
    },

    /** 共通エラー画面処理 */
    commonError() {
      // 共通エラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.COMMON,
        this.requirementId,
        null, // 共通エラーはnull
        async () => {
          // 停止時に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** 変換情報、初期値を取得 */
    async getInitInfo() {
      // 変換情報を取得
      const convRes = await this.getATConversion([this.activeTestId]);
      if (!convRes) {
        // 処理を中断
        return;
      }
      this.conversionInfoList = convRes.data.conversion_list;

      // データ変換情報を取得
      const conversionInfo = this.getConversionInfo();

      if (this.requirementId === "REQ.ACT.SP5" || this.requirementId === "REQ.ACT.SP31") {
        // REQ.ACT.SP5の場合はRID
        this.didList = [];

        // RIDの定義に従ったListを作成
        // label = 10進数の文字列、val = 16進数の文字列
        const i18nList = [];
        for (let i = parseInt(conversionInfo.info[PARAM.RANGE][PARAM.MIN], 16); i <= parseInt(conversionInfo.info[PARAM.RANGE][PARAM.MAX], 16); i++) {
          i18nList.push({ label: i.toString(), val: i.toString(16).padStart(2, "0") });
        }
        this.inputValI18nList = i18nList;

        this.setSliderSetting(
          this.selectedAtFunc,
          conversionInfo,
          this.inputValI18nList
        );

        this.userInputStep = 0; // スライダーの入力値としての最小値(操作規定のMINとは異なる)
      }
      else if (this.requirementId === "REQ.ACT-11" || this.requirementId === "REQ.ACT-26") {
        // REQ.ACT-11, REQ.ACT-26の場合はRID
        this.didList = [];

        // RIDの定義に従ったListを作成
        const i18nList = [];
        for (
          let i = parseInt(conversionInfo.info[PARAM.RANGE][PARAM.MIN], 10);
          i <= parseInt(conversionInfo.info[PARAM.RANGE][PARAM.MAX], 10);
          i++
        ) {
          i18nList.push({ label: i.toString(), val: i.toString() });
        }
        this.inputValI18nList = i18nList;

        this.setSliderSetting(
          this.selectedAtFunc,
          conversionInfo,
          this.inputValI18nList
        );

        this.userInputStep = 0; // スライダーの入力値としての最小値(操作規定のMINとは異なる)
      }
      else {
        // didList
        const didList = this.conversionInfoList.map((conv) => {
          return conv[PARAM.DID];
        });
        this.didList = didList;

        // 入力値の翻訳表を取得
        this.inputValI18nList = this.getDidI18nList(conversionInfo);

        // スライダー設定値の設定
        this.setSliderSetting(
          this.selectedAtFunc,
          conversionInfo,
          this.inputValI18nList
        );

        // 初期値の取得
        const val = await this.getVal(
          this.requirementId,
          conversionInfo,
          this.controlState[0], // スライダーはcontrolStateの長さが1の想定
          this.slider.range
        );

        this.userInputStep = val;
      }

      this.flow = FLOW.STANDBY;
    },

    async getVal(requirementId, conversionInfo, controlState, sliderRange) {
      let convertedVal = "";

      if (controlState[PARAM.VAL]) {
        // Control Stateをパースする
        const tmpControlStateValue = atUtil.parseControlState(this.requirementId, this.activeTestId, controlState[PARAM.VAL], this.byteSize(), conversionInfo.info);

        // 通信値を表示値に変換
        convertedVal = atUtil.convCtrlStateValue(tmpControlStateValue, conversionInfo);

      } else if (controlState[PARAM.DID]) {
        // doReadで値取得
        const res = await this.doRead();

        if (
          !res ||
          !res.data ||
          REQUEST_STATUS_FAILED === res.data.status
        ) {
          // 異常終了時は最小値を取得
          if (DATA_TYPE.VAL === conversionInfo.info[PARAM.TYPE]) {
            convertedVal = this.slider.range.minVal;
          } else {
            convertedVal = this.slider.range.min;
          }
        } else {
          // データ読み出し実行フラグをtrueに切り替え
          this.$emit("toggle-read-executed", true);

          // 返却値は１つの想定
          const readVal = atUtil.getMonitoringStatus(
            res.data[PARAM.USER_DATA][0][PARAM.VAL],
            this.requirementId
          );

          // 通信値を表示値に変換
          convertedVal = atUtil.convCtrlStateValue(readVal, conversionInfo);
        }
      }

      let ret = 0;
      if (DATA_TYPE.FLG === conversionInfo.info[PARAM.TYPE]) {
        // 変換値FLG：00 or FF
        ret = parseInt(convertedVal, 16);
      } else if (
        DATA_TYPE.HEX === conversionInfo.info[PARAM.TYPE] ||
        (
          DATA_TYPE.VAL === conversionInfo.info[PARAM.TYPE] &&
          this.requirementId === "REQ.ACT.SP9"
        )
      ) {
        let index = this.inputValI18nList.findIndex(
          (e) => Number(e.val) === parseInt(convertedVal, 16)
        );
        if (index === -1) {
          let maxVal = null;
          this.inputValI18nList.forEach((x) => {
            if (maxVal === null) {
              maxVal = x.val;
            } else {
              if (maxVal < x.val) {
                maxVal = x.val;
              }
            }
          });
          if ((maxVal !== null) && (maxVal < parseInt(convertedVal, 16))) {
            index = sliderRange.max;
          } else {
            index = sliderRange.min;
          }
        }
        ret = index;
      } else {
        // 変換値をステップ数に変換
        ret = Math.round(
          (convertedVal - this.getNumVal(sliderRange.minVal)) /
          sliderRange.stepVal
        );
      }

      ret = ret < sliderRange.min ? sliderRange.min
        : sliderRange.max < ret ? sliderRange.max : ret;
      return ret;
    },

    setSliderSetting(
      selectedAtFunc,
      conversionInfo,
      inputValI18nList
    ) {
      let step;
      let max;
      let min;
      switch (selectedAtFunc[PARAM.ID_REQUIREMENT]) {
        case "REQ.ACT.SP5":
        case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
        case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
        case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
        case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
        case "REQ.ACT.SP3":
        case "REQ.ACT.SP8":
        case "REQ.ACT.SP9":
        case "REQ.ACT.SP11":
        case "REQ.ACT.SP12":
        case "REQ.ACT.SP13":
        case "REQ.ACT.SP14":
        case "REQ.ACT.SP17":
        case "REQ.ACT.SP31":
        default: {
          // 変換情報のデータを取得
          // （DataTypeがFLG/HEXの場合にキーが存在しなかったため判定を追加）
          if (PARAM.RANGE in conversionInfo.info) {
            step = dataConversion.convertPhysicalValueData(
              conversionInfo.info[PARAM.RANGE][PARAM.STEP],
              conversionInfo.info
            );
            max = dataConversion.convertPhysicalValueData(
              conversionInfo.info[PARAM.RANGE][PARAM.MAX],
              conversionInfo.info
            );
            min = dataConversion.convertPhysicalValueData(
              conversionInfo.info[PARAM.RANGE][PARAM.MIN],
              conversionInfo.info
            );
          }

          break;
        }
      }

      const slider = {};

      // 単位
      slider.unit = this.getUnit(conversionInfo.info);
      // 選択範囲
      slider.range = this.getSliderRange(
        conversionInfo.info[PARAM.TYPE],
        min,
        max,
        step,
        inputValI18nList,
        selectedAtFunc
      );
      // 目盛り
      slider.option = this.getSliderOption(
        conversionInfo.info[PARAM.TYPE],
        slider.range.min,
        slider.range.max
      );

      this.slider = slider;
    },

    /** スライダーの最大、最小、ステップを取得 */
    getSliderRange(data_type, min, max, step, inputValI18nList) {
      const range = {};
      if (
        // didに対応したスライダーの最大値、ステップ数を取得
        DATA_TYPE.HEX === data_type ||
        this.requirementId === "REQ.ACT.SP9" ||
        this.requirementId === "REQ.ACT.SP12"
      ) {
        range.min = 0;
        range.step = 1;
        range.max = inputValI18nList.length - 1;
      } else if (DATA_TYPE.FLG === data_type) {
        // デジタル表示00、FF
        range.min = 0;
        range.max = this.byteSize();
        range.step = this.byteSize();
      } else if (DATA_TYPE.VAL === data_type) {
        range.minVal = min;
        range.maxVal = max;
        range.stepVal = step;
        range.min = 0;
        range.max = Math.round((max - min) / step);
        range.step = 1;
      }
      return range;
    },

    /** スライダーの目盛りを取得 */
    getSliderOption(data_type, min, max) {
      const numMax = Number(max);
      const numMin = Number(min);
      let option = [];
      if (DATA_TYPE.FLG === data_type) {
        // 最大値、最小値のみ
        option = this.getScaleDividedByN(numMin, numMax, 1);
      } else {
        // 分割する
        const tmp = Math.round(numMax - numMin);
        if (tmp < 10) {
          // すべてのステップに目盛りを表示
          option = this.getScaleDividedByN(numMin, numMax, tmp);
        } else if (tmp < 19) {
          // 2分割する目盛りを表示
          option = this.getScaleDividedByN(numMin, numMax, 2);
        } else {
          // 4分割する目盛りを表示
          option = this.getScaleDividedByN(numMin, numMax, 4);
        }
      }
      return option;
    },

    /**
     * スライダーをN分割する目盛り設定用のオブジェクトを作成する。
     * 構造は以下の通り。
     * [{value: 目盛りを表示する値}, ...]
     * @param {number} min ステップ最小値
     * @param {number} max ステップ最大値
     * @param {number} n 分割数
     * @returns 目盛り設定用のオブジェクト
     */
    getScaleDividedByN(min, max, n) {
      const option = [];
      const len = Math.round(max - min);
      for (let i = 0; i <= n; i++) {
        option.push({ value: Math.round(len * i / n) });
      }
      return option;
    },

    getUnit(info) {
      if (DATA_TYPE.VAL === info[PARAM.TYPE])
        return info[PARAM.UNIT]
          ? UNIT_STR.PRE + this.$t(`didUnit.` + info[PARAM.UNIT]) + UNIT_STR.AF
          : UNIT_STR.NONE;
      else return "";
    },

    getSliderValue() {
      if (this.userInputStep === undefined || this.userInputStep === null) return null;
      if (
        !this.slider.range[PARAM.STEP] ||
        !this.slider.range[PARAM.MIN] === undefined ||
        !this.slider.range[PARAM.MIN] === null
      ) {
        return null;
      }
      if (this.slider.range.max === this.userInputStep) {
        return fixNumberWithDecimal(this.getNumVal(this.slider.range.maxVal), this.conversionInfoList[0].info.digit_after_decimal);
      }

      const value =
        this.getNumVal(this.slider.range.minVal) +
        Number(this.userInputStep) * this.getNumVal(this.slider.range.stepVal);

      return fixNumberWithDecimal(value, this.conversionInfoList[0].info.digit_after_decimal);
    },

    /**
     * 翻訳ファイルからdidに対応した表示値リストを取得
     * @param {*} conversionInfo
     * @returns {Array} [{label:触媒前導入, val:1}]
     */
    getDidI18nList(conversionInfo) {
      const did = conversionInfo[PARAM.DID];
      const data_type = conversionInfo.info[PARAM.TYPE];
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, this.individualMenus ? this.individualMenus.menu_list : []);
      const list = [];
      let uiid = null;
      if (DATA_TYPE.HEX === data_type) {
        uiid = this.activeTestId.slice(0, -2);
        if (uiid === UIID.ACC) {
          // オルタネータ制御コントロールの場合
          const hexList = [ACC_VALS.LOW, ACC_VALS.MID, ACC_VALS.HIGH, ACC_VALS.EXHIGH];
          for (const hex of hexList) {
            const intValue = parseInt(hex, 16);
            // 表示範囲外の場合は次へ
            if (
              intValue < conversionInfo.info[PARAM.OPERATION_RULES].min ||
              conversionInfo.info[PARAM.OPERATION_RULES].max < intValue
            ) {
              continue;
            }

            // diag/配下の検索キー
            const key = `${findFFDi18nKey}.${did}_${hex}`;
            // at/did/配下の検索キー
            const keyAt = `atDid${systemId}.${did}_${hex}`;

            if (this.$te(key)) {
              list.push({ label: this.$t(key), val: intValue });
            } else if (this.$te(keyAt)) {
              list.push({ label: this.$t(keyAt), val: intValue });
            } else {
              // 翻訳が取得できなかった場合は、HEX値を直接表示
              list.push({ label: `${HEX_PREFIX}${hex}`, val: intValue });
            }
          }
        } else if (uiid === UIID.ENF) {
          // 送信値を取得してリストにプッシュ
          SP12_VALS[uiid].forEach((x) => {
            // at/did/配下の検索キー
            const keyAt = `atDid${systemId}.${x}`;
            list.push({ label: this.$t(keyAt), val: x });
          });
        } else {
          const dataLen = 16 ** this.byteSize();
          // バイト数だけ取得
          for (let dec = 0; dec <= dataLen; dec++) {
            const hexKey = dec
              .toString(16)
              .toUpperCase()
              .padStart(this.byteSize() * 2, "0");

            // diag/配下の検索キー
            const key = `${findFFDi18nKey}.${did}_${hexKey}`;
            // at/did/配下の検索キー
            const keyAt = `atDid${systemId}.${did}_${hexKey}`;

            if (this.$te(key)) {
              // SP8とSP13の場合、自由表示列で「-」指定されているものを除く
              if (this.isIncludeSliderValue(this.$t(key))) {
                list.push({ label: this.$t(key), val: dec });
              }
            } else if (this.$te(keyAt)) {
              // SP8とSP13の場合、自由表示列で「-」指定されているものを除く
              if (this.isIncludeSliderValue(this.$t(keyAt))) {
                list.push({ label: this.$t(keyAt), val: dec });
              }
            } else {
              // 取得できなかった段階で以降取得取りやめ
              break;
            }
          }
        }

      } else if (DATA_TYPE.FLG === data_type) {
        // did_00を取得
        // diag/配下の検索キー
        const key0 = `${findFFDi18nKey}.${did}_${"00"}`;
        // at/did/配下の検索キー
        const key0At = `atDid${systemId}.${did}_${"00"}`;

        if (this.$te(key0)) {
          list.push({ label: this.$t(key0), val: 0 });
        } else if (this.$te(key0At)) {
          list.push({ label: this.$t(key0At), val: 0 });
        } else {
          switch (this.requirementId) {
            case "REQ.ACT-9":
            default:
              list.push({ label: "0", val: 0 });
          }
        }

        // did_FFを取得
        const keyFF = `${findFFDi18nKey}.${did}_${"FF"}`;
        // at/did/配下の検索キー
        const keyFFAt = `atDid${systemId}.${did}_${"FF"}`;

        if (this.$te(keyFF)) {
          list.push({ label: this.$t(keyFF), val: this.byteSize() });
        } else if (this.$te(keyFFAt)) {
          list.push({ label: this.$t(keyFFAt), val: this.byteSize() });
        } else {
          switch (this.requirementId) {
            case "REQ.ACT-9":
            default:
              list.push({ label: `${this.byteSize()}`, val: this.byteSize() });
          }
        }

      } else if (DATA_TYPE.VAL === data_type) {
        // UIIDが一致すれば対応する値の配列を取る
        uiid = this.activeTestId.slice(0, -2);
        if ([UIID.RSRH, UIID.LSRH, UIID.RADB, UIID.LADB].includes(uiid)) {
          // 送信値を算出してリストにプッシュ
          SP9_VALS[uiid].forEach((value) => {
            const sendData = atUtil.convertValueToHex(value, conversionInfo);
            list.push({ label: value, val: sendData });
          });
        }
      }

      return list;
    },

    /** 
     * スライダーの入力値に含めるか否かの判定
     * @param {string} value 値
     * @returns スライダーの入力値に含めるか否か(true:含める)
     */
    isIncludeSliderValue(value) {
      const EXCLUDE_REQUIREMENT_ID = ["REQ.ACT.SP8", "REQ.ACT.SP13"];
      const EXCLUDE_VALUE = "-";
      return (!EXCLUDE_REQUIREMENT_ID.includes(this.requirementId) ||
        (EXCLUDE_REQUIREMENT_ID.includes(this.requirementId) && !EXCLUDE_VALUE.includes(value)));
    },

    /** 
     * エラー処理
     * @async
     * @param {*} errKey エラーキー
     * @param {string} [atMsg=null] STATUSメッセージ
     */
    async doErrorProcess(errKey, atMsg = null, isExecution) {
      switch (errKey) {
        case ERR_KEY.COMMON:
          break;
        case ERR_KEY.STATUS:
        case ERR_KEY.COMMUNICATION:
        case ERR_KEY.NRC:
        default:
          {
            // SP13以外は停止処理を実施する
            if (this.requirementId !== "REQ.ACT.SP13") {
              const response = await this.doStop(isExecution, atUtil.isStatusOrNrcErrorKey(errKey));
              if (response) {
                if (response.data.status === REQUEST_STATUS_COMPLETED) {
                  let checkResponse = true;
                  if (response.data.user_data && Array.isArray(response.data.user_data)) {
                    for (const userData of response.data.user_data) {
                      if (userData.res_type !== RES_TYPE.OK) {
                        checkResponse = false;
                        break;
                      }
                    }
                  }

                  // NRC/STATUSエラー後の停止失敗は、元のエラーを優先する
                  if (!checkResponse && !atUtil.isStatusOrNrcErrorKey(errKey)) {
                    // 共通エラーへ遷移
                    this.flow = FLOW.ERR;
                    this.commonError();
                    return;
                  }
                }
              }
            }
          }
          break;
      }

      // エラーダイアログの[OK]ボタン押下時の処理
      const afterMethod = async () => {
        this.displayProcessing = true;
        await atUtil.doTerminate(this, true);
        this.displayInit();
        this.$emit('at-failure');
        this.displayProcessing = false;
      };

      // エラーダイアログ表示
      atUtil.showUnexpectedError(
        this,
        errKey,
        this.requirementId,
        atMsg,
        afterMethod
      );
      return;
    },

    /**
     * ユーザ入力値を16進数に変換
     * 最小値を00とし、255ステップをFFとする（最大値がFF未満になる）
     * @returns 00~FF
     */
    getHexSliderValue() {
      // DID変換情報を取得
      const conversionInfo = this.getConversionInfo();

      let val = "";

      if (
        this.dataType === DATA_TYPE.HEX &&
        this.requirementId === "REQ.ACT.SP5"
      ) {
        // RIDを参照する特殊なパターン(SP5)
        // ステップのインデックスを取得（CCへ送信する通信値に対応）
        const ele = this.inputValI18nList[this.userInputStep];
        val = ele.val;

        // バイトに変換
        val = val.toString(16).toUpperCase().padStart(2, "0");
      } else if (this.dataType === DATA_TYPE.HEX &&
        this.requirementId === "REQ.ACT.SP12"
      ) {
        this.controlState[0][PARAM.DID] = SP12_VALS[UIID.ENF][this.userInputStep];
        val = this.controlState[0][PARAM.VAL];
      } else if (
        this.dataType === DATA_TYPE.FLG &&
        this.requirementId === "REQ.ACT-9"
      ) {
        val = this.inputValI18nList[this.userInputStep].val;
        let padSize = 0;
        for (const item of this.conversionInfoList) {
          padSize += item.info.byte_or_bit === "byte" ? item.info.byte_size : item.info.bit_size / 8;
        }
        val = val.toString(16).toUpperCase().padStart(Math.ceil(padSize) * 2, "0");
      } else if (
        this.dataType === DATA_TYPE.FLG ||
        this.dataType === DATA_TYPE.HEX ||
        (
          this.dataType === DATA_TYPE.VAL &&
          this.requirementId === "REQ.ACT.SP9"
        )
      ) {
        // inputValI18nListから16進値を取得するパターン
        // ステップのインデックスを取得（CCへ送信する通信値に対応）
        const ele = this.inputValI18nList[this.userInputStep];
        val = ele.val;

        // バイトに変換
        const padSize = this.byteSize() * 2;
        val = val.toString(16).toUpperCase().padStart(padSize, "0");

        // リトルエンディアンの場合はバイト反転
        if (conversionInfo.info[PARAM.LITTLE_ENDIAN]) {
          val = atUtil.reverseBytes(val, conversionInfo);
        }

        if (this.dataType === DATA_TYPE.FLG) {
          // OFF以外の場合（つまりONの場合）
          if (val !== ("0".repeat(padSize))) {
            // byteタイプの場合、"F" * Size
            if (conversionInfo.info[PARAM.BYTE_OR_BIT] === "byte") {
              val = "F".repeat(padSize);
            }
            // bitタイプの場合、controlState値をそのまま
            else {
              val = this.controlState[0][PARAM.VAL];
            }
          }
        }

      } else if (this.dataType === DATA_TYPE.VAL) {
        // 数値から変換情報を用いてHEXにするパターン
        // CCへ送信する入力値を算出（データ変換前の値で算出）
        const min = Number(conversionInfo.info[PARAM.RANGE][PARAM.MIN]);
        const stepVal = Number(conversionInfo.info[PARAM.RANGE][PARAM.STEP]);
        val = min + (stepVal * this.userInputStep);

        // 変換情報をもとに数値をHEXに逆変換
        val = atUtil.convertValueToHex(val, conversionInfo);
      }

      // controlState値がnullの場合はそのまま返却
      if (!this.controlState[0]) {
        return val;
      }
      // DIDタイプの場合、または"bit"タイプ且つFLGタイプの場合
      else if (this.controlState[0][PARAM.DID] ||
        (conversionInfo.info[PARAM.BYTE_OR_BIT] === "bit" && this.dataType === DATA_TYPE.FLG)) {
        if (this.requirementId === "REQ.ACT-9") {
          // ACT-9の場合は、FLGタイプであってもControl Stateを組み立ててから返却
          return atUtil.buildControlState(this.requirementId, this.activeTestId, val, this.controlState[0][PARAM.VAL], conversionInfo.info);
        }
        // 上記以外はそのまま返却
        else return val;
      }
      // それ以外の場合は送信用のControl Stateを組み立ててから返却
      else {
        return atUtil.buildControlState(this.requirementId, this.activeTestId, val, this.controlState[0][PARAM.VAL], conversionInfo.info);
      }
    },

    /* パラメータat_statusに格納するオブジェクトを返す */
    getAtStatus() {
      // HEV系ATのDM１行目にControl結果表示かつDM実行中のみ設定
      return this.isHev && this.inExecution && this.baseTime ? {
        project_id: parseInt(this.projectId),
        registered_at: this.baseTime,
        elapsed_time: (Date.now() - this.baseTime) * 1000
      } : null;
    },

    /** AT文言の削除 */
    clearAtMsg() {
      this.message = "";
    },

    /******************** 以下API ********************/

    /**
     * 変換情報取得処理
     * @param {array} at_id_list 選択項目の不変idList
     * @returns {array} conversionInfo 変換情報
     */
    async getATConversion(at_id_list) {
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await api.getParamCall(
            config.DMAT,
            "/netApps/" +
            this.netAppId +
            "/systems/" +
            this.systemId +
            "/activeTestConversion",
            {
              active_test_id_list: JSON.stringify(at_id_list),
            }
          );
        },
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // monitor_items が入っていれば成功とする
          return getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.conversion_list
            ? ""
            : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        }
      );

      return response;
    },

    /** データ読出し */
    async doRead() {
      if (!atUtil.needsReadByReqId(this.requirementId)) return null;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        did: [this.selectedAtFunc[PARAM.CTRL_STATE][0][PARAM.DID]],
        type: API_TYPE.READ,
      };

      // read API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        }
      );
      if (response) {
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            if (response.data[PARAM.USER_DATA][0][PARAM.RES_TYPE] !== "ok") {
              if (this.requirementId !== "REQ.ACT.SP17") {
                this.doErrorProcess(ERR_KEY.COMMON);
              }
            } else {
              if (this.requirementId === "REQ.ACT.SP3") {
                if (response.data.user_data[0].value.slice(6, 10) === "1011") {
                  await this.doRead();
                } else {
                  atUtil.showUnexpectedError(
                    this,
                    ERR_KEY.COMMUNICATION,
                    this.requirementId,
                    null, // 共通エラーはnull
                    async () => {
                      // 停止時に他の操作をさせないようにする
                      this.displayProcessing = true;
                      await atUtil.doTerminate(this, true);
                      this.displayInit();
                      this.$emit('at-failure');
                      this.displayProcessing = false;
                    }
                  );
                }
              }
            }
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            break;
          default:
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return response;
    },

    /**
     * 駆動開始
     * @returns true:停止処理へ移動、false:ユーザの停止操作を待つ
     */
    async doStart() {
      this.isStopping = false;

      // API実行パラメータ作成
      const token = session.getToken();
      const val = this.getHexSliderValue();
      const atStatus = this.getAtStatus();
      const requestBody = atUtil.genReqBodyStart(
        token.id_token,
        this.selectedAtFunc,
        val,
        undefined,
        atStatus
      );

      // start API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          this.flow = FLOW.EXECUTE;
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          if (atUtil.isErrorAbort(getApiResponse.data[PARAM.REQUEST_RESULT])) {
            // 開始のポーリングでCCエラー中断のレスポンスは、データモニタでエラーとなった場合のため、通信エラーへ遷移
            // ただしデータモニタ起点で既にエラー処理をおこなっている場合は、そのままポーリング終了
            if (!FLOW_ERROR.includes(this.flow)) {
              this.flow = FLOW.ERR;
              this.doErrorProcess(ERR_KEY.COMMUNICATION);
            }
            return config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }

          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isExeOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isExeOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 実行
            if (isExeOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              if (self.isStopping) {
                // 停止処理が開始されたのでflowは遷移させない
              } else {
                // 実行監視に遷移
                self.flow = FLOW.CHECK_EXE;
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case "REQ.ACT.SP3":
                  // 通信エラーへ遷移
                  this.flow = FLOW.ERR;
                  this.doErrorProcess(ERR_KEY.COMMUNICATION, null, true);
                  break;
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
                case "REQ.ACT.SP9":
                case "REQ.ACT.SP12":
                case "REQ.ACT.SP31": {
                  // 通信エラーへ遷移
                  this.flow = FLOW.ERR;
                  this.doErrorProcess(ERR_KEY.COMMUNICATION);
                  break;
                }

                case "REQ.ACT.SP5": {
                  // STATUSエラーへ遷移
                  this.flow = FLOW.ERR_STATUS;
                  this.doErrorProcess(ERR_KEY.STATUS);
                  break;
                }
                case "REQ.ACT.SP11":
                case "REQ.ACT.SP13": {
                  // NRC0x22ならNRCエラー、それ以外なら共通エラーに遷移
                  let errCode = ERR_KEY.COMMON;
                  this.flow = FLOW.ERR;
                  if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                    if (nrc === NRC_CODE.X22) {
                      errCode = ERR_KEY.NRC;
                      this.flow = FLOW.ERR_NRC;
                    }
                  }
                  this.doErrorProcess(errCode);
                  break;
                }
                case "REQ.ACT-9":
                case "REQ.ACT-11":
                case "REQ.ACT-26": {
                  // NRC0x22ならNRCエラー、それ以外なら通信エラーに遷移
                  let errCode = ERR_KEY.COMMUNICATION;
                  self.flow = FLOW.ERR;
                  if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                    if (nrc === NRC_CODE.X22) {
                      errCode = ERR_KEY.NRC;
                      self.flow = FLOW.ERR_NRC;
                    }
                  }
                  // エラーダイアログ表示
                  atUtil.showUnexpectedError(
                    self,
                    errCode,
                    self.requirementId,
                    undefined,
                    async () => {
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  break;
                }
                case "REQ.ACT.SP14": {
                  // NRC0x22ならNRCエラー、それ以外なら通信エラーに遷移
                  let errCode = ERR_KEY.COMMUNICATION;
                  this.flow = FLOW.ERR;
                  if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                    if (nrc === NRC_CODE.X22) {
                      errCode = ERR_KEY.NRC;
                      this.flow = FLOW.ERR_NRC;
                    }
                  }
                  this.doErrorProcess(errCode);
                  break;
                }
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP8":
                case "REQ.ACT.SP17":
                default: {
                  // 共通エラーに遷移
                  self.flow = FLOW.ERR;
                  this.doErrorProcess(ERR_KEY.COMMON);
                  break;
                }
              }
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 実行監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              // 正常終了
              const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
              switch (self.requirementId) {
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
                case "REQ.ACT.SP5":
                case "REQ.ACT.SP8":
                case "REQ.ACT.SP11":
                case "REQ.ACT.SP14":
                case "REQ.ACT.SP31":
                  if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE].includes(
                      status
                    )
                  ) {
                    // 実行監視継続条件
                    if (self.requirementId === "REQ.ACT.SP5"
                      && getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE
                    ) {
                      // 継続条件に合致 && monitoring_status:completeの場合、応答回数が2回超えたとして、STATUSエラーへ遷移
                      const atMsg = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      self.message = atMsg;
                      this.flow = FLOW.ERR_STATUS;
                      this.doErrorProcess(ERR_KEY.STATUS, atMsg);
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    } else {
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    }
                  } else if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE].includes(status)
                  ) {
                    // 実行監視終了条件
                    if (self.requirementId === "REQ.ACT.SP5") {
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                    }
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // それ以外
                    const atMsg = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    // STATUSエラーへ遷移
                    this.flow = FLOW.ERR_STATUS;
                    this.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP13":
                case "REQ.ACT.SP17":
                case "REQ.ACT-9":
                case "REQ.ACT-11": {
                  // 実行中/出力中
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(status, 16),
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                }
                case "REQ.ACT-26": {
                  // AT文言に駆動を表示して、停止待機 ※ルーチン実行は1回のみで、ユーザーの停止指示待ちの状態
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    true
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT.SP12": {
                  // 発音中
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    this.controlState[0][PARAM.VAL],
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                }
                case "REQ.ACT.SP9": {
                  if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE].includes(status)
                  ) {
                    // 実行監視終了条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    // ユーザの「停止」操作を待つ
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // それ以外
                    // 通信エラーへ遷移
                    this.flow = FLOW.ERR;
                    this.doErrorProcess(ERR_KEY.COMMUNICATION);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                case "REQ.ACT.SP3": {
                  const value = getApiResponse.data.user_data[0].value.slice(6, 10);
                  if (value === "0001") {
                    return this.doMonitor();
                  } else if (value === "0000" || value === "1011") {
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    const atMsg = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    // STATUSエラーへ遷移
                    this.flow = FLOW.ERR_STATUS;
                    this.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                default:
                  break;
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case "REQ.ACT.SP5": {
                  // STATUSエラーへ遷移
                  this.flow = FLOW.ERR_STATUS;
                  this.doErrorProcess(ERR_KEY.STATUS);
                  break;
                }
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP8":
                case "REQ.ACT.SP11":
                case "REQ.ACT.SP17": {
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  this.doErrorProcess(ERR_KEY.COMMON);
                  break;
                }
                case "REQ.ACT.SP13": {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                  if (nrc === NRC_CODE.X22) {
                    // NRC0x22の場合、NRCエラーへ遷移
                    this.flow = FLOW.ERR_NRC;
                    this.doErrorProcess(ERR_KEY.NRC);
                  } else {
                    // 共通エラーへ遷移
                    this.flow = FLOW.ERR;
                    this.doErrorProcess(ERR_KEY.COMMON);
                  }
                  break;
                }
                case "REQ.ACT-9":
                case "REQ.ACT-11": {
                  // NRC0x22ならNRCエラー、それ以外なら通信エラーに遷移
                  let errCode = ERR_KEY.COMMUNICATION;
                  self.flow = FLOW.ERR;
                  if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                    if (nrc === NRC_CODE.X22) {
                      errCode = ERR_KEY.NRC;
                      self.flow = FLOW.ERR_NRC;
                    }
                  }
                  // エラーダイアログ表示
                  atUtil.showUnexpectedError(
                    self,
                    errCode,
                    self.requirementId,
                    undefined,
                    async () => {
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  break;
                }
                case "REQ.ACT-26": {
                  if ([NRC_CODE.X21, NRC_CODE.X24].includes(atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]))) {
                    // 否定応答かつNRC0x21/0x24:ポーリング継続
                    // 停止ボタン押下可能に変更。但し、停止中の場合は実行状態を維持する(停止監視中に停止ボタンが押下できてしまうため)
                    if (!self.isStopping) { self.flow = FLOW.CHECK_EXE; }
                    // AT文言：停止中は「停止中」を維持し、停止中以外は「駆動」
                    self.message = self.isStopping
                      ? self.$t("individual.label_active_test_message_hev_stopping")
                      : atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        null,
                        true
                      );
                  } else {
                    // 上記以外：「停止」表示、共通エラーへ遷移
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      null,
                      false
                    );
                    this.flow = FLOW.ERR;
                    this.doErrorProcess(ERR_KEY.COMMON);
                  }
                  break;
                }
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
                case "REQ.ACT.SP3":
                case "REQ.ACT.SP12":
                case "REQ.ACT.SP31":
                default: {
                  // 通信エラーへ遷移
                  this.flow = FLOW.ERR;
                  this.doErrorProcess(ERR_KEY.COMMUNICATION);
                  break;
                }
              }
              // REQ-26のみ否定応答かつNRC0x21/0x24の場合は実行監視を継続
              if (self.requirementId === "REQ.ACT-26"
                && [NRC_CODE.X21, NRC_CODE.X24].includes(atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]))) {
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
              } else {
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              }
            }
          }
          if (self.isStopping) {
            // 停止処理が開始されたのでstartのポーリングを終了させる
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーに遷移
          self.flow = FLOW.ERR;
          this.doErrorProcess(ERR_KEY.COMMON);
        },
      );

      if (response) {
        const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[PARAM.USER_DATA]);
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // completed
            if (
              response.data[PARAM.USER_DATA].length > 0 &&
              monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              switch (this.requirementId) {
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP5":
                case "REQ.ACT.SP8":
                case "REQ.ACT.SP11":
                case "REQ.ACT.SP12":
                case "REQ.ACT.SP13":
                case "REQ.ACT.SP14":
                case "REQ.ACT.SP31":
                  // 停止へ遷移
                  if (!this.isStopping) {
                    this.flow = FLOW.STOP;
                    await this.stopDrive();
                  }
                  return true;
                default:
                  break;
              }
            } else {
              // 異常終了
              // completed時のエラー処理はコールバックで行う
            }
            break;
          case REQUEST_STATUS_FAILED: {
            // エラー
            const errKey = atUtil.getErrKeyByAsyncCd(response.data.async_code);
            this.doErrorProcess(errKey);
            break;
          }
          default:
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return false;
    },

    /**
      * 実行監視(実行中監視)
      * @returns true:停止処理へ移動、false:ユーザの停止操作を待つ
      */
    async doMonitor() {
      this.isStopping = false;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        did: this.selectedAtFunc.sid0x2E_sid0x2F[0],
        type: API_TYPE.MONITOR,
      };

      // monitor API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          this.flow = FLOW.EXECUTE;
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isMonitorOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isMonitorOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 実行
            if (isMonitorOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              if (self.isStopping) {
                // 停止処理が開始されたのでflowは遷移させない
              } else {
                // 実行監視に遷移
                self.flow = FLOW.CHECK_EXE;
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
              }
            } else {
              // 異常終了
              // STATUSエラーへ遷移
              this.flow = FLOW.ERR_STATUS;
              this.doErrorProcess(ERR_KEY.STATUS);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 実行監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              // 正常終了
              const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
              // 実行監視継続条件
              const value = getApiResponse.data.user_data[0].value.slice(6, 10);
              if (value === "0000" || value === "0001") {
                self.message = atUtil.getAtMsg(
                  self,
                  self.systemId,
                  self.requirementId,
                  parseInt(status, 16),
                  self.isDriveFlow
                );
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
              } else if (value === "1011") {
                self.message = self.$t("individual.label_active_test_message_sp3_not_execute");
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
              } else {
                // それ以外
                const atMsg = atUtil.getAtMsg(
                  self,
                  self.systemId,
                  self.requirementId,
                  parseInt(status, 16),
                  self.isDriveFlow
                );
                // STATUSエラーへ遷移
                this.flow = FLOW.ERR_STATUS;
                this.doErrorProcess(ERR_KEY.STATUS, atMsg);
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              }
            } else {
              // 異常終了
              // 通信エラーへ遷移
              this.flow = FLOW.ERR;
              this.doErrorProcess(ERR_KEY.COMMUNICATION);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          if (self.isStopping) {
            // 停止処理が開始されたのでstartのポーリングを終了させる
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーに遷移
          self.flow = FLOW.ERR;
          this.doErrorProcess(ERR_KEY.COMMON);
        },
      );

      if (response) {
        const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[PARAM.USER_DATA]);
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // completed
            if (
              response.data[PARAM.USER_DATA].length > 0 &&
              monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              // 停止へ遷移
              if (!this.isStopping) {
                this.flow = FLOW.STOP;
                await this.stopDrive();
              }
              return true;
            } else {
              // 異常終了
              // completed時のエラー処理はコールバックで行う
            }
            break;
          case REQUEST_STATUS_FAILED: {
            // エラー
            const errKey = atUtil.getErrKeyByAsyncCd(response.data.async_code);
            this.doErrorProcess(errKey);
            break;
          }
          default:
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return false;
    },

    /**
     * AT停止処理
     * stopAPI失敗時の挙動をignoreStopErrorで制御する
     * @param {boolean} isExecution 実行中かどうか
     * @param {boolean} ignoreStopError stopAPI失敗時にエラーを無視するかどうか
     * @returns {object} APIレスポンス
     */
    async doStop(isExecution, ignoreStopError = false) {
      this.isStopping = true;

      // REQ.ACT-26の場合、AT文言：「停止中」
      if (this.requirementId === "REQ.ACT-26") {
        this.message = this.$t("individual.label_active_test_message_hev_stopping");
      }

      // API実行パラメータ作成
      const token = session.getToken();
      const atStatus = this.getAtStatus();
      let errorType;
      if (isExecution === true) {
        errorType = ERR_TYPE.COMMUNICATE;
      } else if (self.flow === FLOW.ERR) {
        errorType = ERR_TYPE.STATUS;
      } else {
        errorType = undefined;
      }
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        undefined,
        errorType,
        atStatus
      );

      // stop API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        () => {
          // NRC/STATUSエラー以外のdoStopの呼び出しの場合
          if (!ignoreStopError) {
            atUtil.showUnexpectedError(this);
          }
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          // NRC/STATUSエラー以外のdoStopの呼び出しの場合
          if (!ignoreStopError) {
            atUtil.validateErrorResult(this, result);
          }
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          // NRC/STATUSエラー以外のdoStopの呼び出しの場合
          if (!ignoreStopError) {
            atUtil.validateErrorResult(this, result);
          }
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          let result = "";
          if (getApiResponse.data[PARAM.REQUEST_RESULT] === REQUEST_RESULT.IGNORE) {
            // 実行監視_終了条件に当てはまる場合Terminateする場合
            // 実行監視_終了条件への遷移と停止要求の同時発生によるエラーを防ぐ処理
            // スライダーで当てはまる機能要求IDは無いが、今後の追加を想定して実装
            // 停止要求が無効だった場合、何もせずに処理を終了する
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            return result;
          }

          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 停止
            if (
              isStopOnly ||
              monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.SP8":
                case "REQ.ACT.SP14":
                case "REQ.ACT-11":
                case "REQ.ACT-26":
                  // 停止監視に遷移
                  self.flow = FLOW.CHECK_STOP;
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.NLP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.RD.LP":
                case "REQ.ACT.2F.IN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP3":
                case "REQ.ACT.SP5":
                case "REQ.ACT.SP9":
                case "REQ.ACT.SP11":
                case "REQ.ACT.SP12":
                case "REQ.ACT.SP13":
                case "REQ.ACT.SP31":
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT.SP17":
                case "REQ.ACT-9":
                  // 停止
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;

                default:
                  break;
              }
            } else {
              // 異常終了
              if (ignoreStopError) {
                // NRC/STATUSエラー時のdoStopの呼び出しの場合
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              } else {
                switch (self.requirementId) {
                  case "REQ.ACT.SP5":
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  case "REQ.ACT-9":
                  case "REQ.ACT-11":
                  case "REQ.ACT-26": {
                    // NRC0x22ならNRCエラー、それ以外なら通信エラーに遷移
                    let errCode = ERR_KEY.COMMUNICATION;
                    self.flow = FLOW.ERR;
                    if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                      const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                      if (nrc === NRC_CODE.X22) {
                        errCode = ERR_KEY.NRC;
                        self.flow = FLOW.ERR_NRC;
                      }
                    }
                    // エラーダイアログ表示
                    atUtil.showUnexpectedError(
                      self,
                      errCode,
                      self.requirementId,
                      undefined,
                      async () => {
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  }
                  default:
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              }
            }
          } else {
            // 停止監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
              switch (self.requirementId) {
                case "REQ.ACT.SP3": {
                  const value = getApiResponse.data.user_data[0].value.slice(6, 10);
                  if (value === "1011") {
                    // 停止監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else if (value === "0000") {
                    // 停止監視終了条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // それ以外
                    if (!ignoreStopError) {
                      // NRC/STATUSエラー時のdoStopの呼び出しでない場合、共通エラーへ遷移
                      self.flow = FLOW.ERR;
                      self.doErrorProcess(ERR_KEY.COMMON);
                    }
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                }
                  break;
                case "REQ.ACT.2F.IN.EXM.STP.STM.RD.NLP":
                  if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status)
                  ) {
                    // 停止監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;

                    // トレーニングモード時は停止監視継続をしない
                    if (this.isTestMode) {
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    }

                  } else if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status)
                  ) {
                    // 停止監視終了条件
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // それ以外
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.SP8":
                  // アクティブテスト文言設定
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(status, 16),
                    self.isDriveFlow
                  );
                  if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status)
                  ) {
                    // 停止監視継続条件
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // それ以外
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.SP14":
                  if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status)
                  ) {
                    // 停止監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // それ以外
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT-11":
                  if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status)
                  ) {
                    // 停止監視継続条件
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else if (
                    self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status)
                  ) {
                    // 停止監視終了条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    if (!ignoreStopError) {
                      // NRC/STATUSエラー時のdoStopの呼び出しでない場合、共通エラーへ遷移
                      self.flow = FLOW.ERR;
                      self.doErrorProcess(ERR_KEY.COMMON);
                    }
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT-26": {
                  if (status === "02") {
                    // AT文言に停止を表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      null,
                      false
                    );
                  } else {
                    if (!ignoreStopError) {
                      // NRC/STATUSエラー時のdoStopの呼び出しでない場合、共通エラーへ遷移
                      self.flow = FLOW.ERR;
                      self.doErrorProcess(ERR_KEY.COMMON);
                    }
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }

                default:
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
              }
            } else {
              // 異常終了
              if (ignoreStopError) {
                // NRC/STATUSエラー時のdoStopの呼び出しの場合
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              } else {
                switch (self.requirementId) {
                  case "REQ.ACT-11": {
                    // NRC0x22ならNRCエラー、それ以外なら通信エラーに遷移
                    let errCode = ERR_KEY.COMMUNICATION;
                    self.flow = FLOW.ERR;
                    if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                      const nrc = atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]);
                      if (nrc === NRC_CODE.X22) {
                        errCode = ERR_KEY.NRC;
                        self.flow = FLOW.ERR_NRC;
                      }
                    }
                    // エラーダイアログ表示
                    atUtil.showUnexpectedError(
                      self,
                      errCode,
                      self.requirementId,
                      undefined,
                      async () => {
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  }
                  case "REQ.ACT-26": {
                    if ([NRC_CODE.X21].includes(atUtil.getNRCStatus(monitoringStatusData[PARAM.VAL]))) {
                      // 否定応答かつNRC0x21:ポーリング継続
                      // AT文言：「停止中」を維持
                      self.message = self.$t("individual.label_active_test_message_hev_stopping");
                      result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    } else {
                      // 上記以外(主にNRC0x24が該当)：「停止」表示、正常終了
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        null,
                        false
                      );
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    }
                    break;
                  }
                  default:
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              }
            }
          }
          if (ignoreStopError && result === config.INDIVIDUAL_POLLING_RESULT_ERROR) {
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // NRC/STATUSエラー以外のdoStopの呼び出しの場合
          if (!ignoreStopError) {
            // 共通エラーに遷移
            self.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
          }
        }
      );

      if (ignoreStopError) {
        // NRC/STATUSエラー時のdoStopの呼び出しの場合
        return response;
      }

      // ここまでの処理でエラーが発生している場合、後続の処理は実施しない
      if (FLOW_ERROR.includes(this.flow)) {
        return response;
      }

      if (response) {
        if (response.data[PARAM.REQUEST_RESULT] === REQUEST_RESULT.IGNORE) {
          // 実行監視_終了条件に当てはまる場合Terminateする場合
          // 実行監視_終了条件への遷移と停止要求の同時発生によるエラーを防ぐ処理
          // スライダーで当てはまる機能要求IDは無いが、今後の追加を想定して実装
          // 停止要求が無効だった場合、何もせずに処理を終了する
          return response;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            await this.doReadAfterStopped(isExecution);
            break;
          case REQUEST_STATUS_FAILED: {
            // エラー
            this.flow = FLOW.ERR;
            const errKey = atUtil.getErrKeyByAsyncCd(response.data.async_code);
            atUtil.showUnexpectedError(
              this,
              errKey,
              this.requirementId,
              response.data.async_message
            );
            break;
          }
          default:
            this.flow = FLOW.ERR;
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return response;
    },

    /** 正常時のAT終了処理 
     * @async
     * @return {boolean} terminate result - false is faild.
     */
    async doTerminate() {
      const response = await atUtil.doTerminate(this, false);
      if (response) {
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 正常終了
          if (!response.data.user_data
            || response.data.user_data.length === 0
            || response.data.user_data[0][PARAM.RES_TYPE] === RES_TYPE.OK) {

            return true;
          }
        }
      }

      return false;
    },

    /** 停止時のRead実行 */
    async doReadAfterStopped(isExecution) {
      if (this.conversionInfoList && this.conversionInfoList.length > 0) {
        const conversionInfo = this.conversionInfoList.find(
          (ci) => ci[PARAM.DID] === this.selectedAtFunc[PARAM.SID0X2E_0X2F][0]
        );

        if (
          this.requirementId === "REQ.ACT.SP5" ||
          this.requirementId === "REQ.ACT.SP31" ||
          this.requirementId === "REQ.ACT-11" ||
          this.requirementId === "REQ.ACT-26" ||
          isExecution === true
        ) {
          this.userInputStep = 0; // スライダーの入力値としての最小値(操作規定のMINとは異なる)
        } else {
          // 初期値の取得
          const val = await this.getVal(
            this.requirementId,
            conversionInfo,
            this.controlState[0], // スライダーはcontrolStateの長さが1の想定
            this.slider.range
          );
          this.userInputStep = val;
        }
      }
    },

    /**
     * データ変換情報を取得する
     */
    getConversionInfo() {
      // スライダーでは変換情報は単一
      for (const convInfo of this.conversionInfoList) {
        if (convInfo[PARAM.DID] === this.selectedAtFunc[PARAM.SID0X2E_0X2F][0] ||
          convInfo[PARAM.RID] === this.selectedAtFunc[PARAM.SID0X2E_0X2F][0]
        ) {
          return convInfo;
        }
      }

      return null;
    },

    /** キャンセル処理 */
    async cancelExec() {
      // 駆動中であれば停止する。ただし他のエラー処理を既に実行していれば何もしない
      if (this.isDriveFlow && !FLOW_ERROR.includes(this.flow)) {
        this.flow = FLOW.STOP;
        await this.doStop();
      }
    },

    setSignout() {
      this.signout = true;
    },
  },

  mounted() {
    // noop
  },
};
